import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { invalidateDashboardCache } from "./dashboardCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Called when the extension signals logout — skips API call to avoid double-hitting /auth/logout
  const silentLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem("cartcontrol_token");
    invalidateDashboardCache();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
    localStorage.removeItem("cartcontrol_token");
    invalidateDashboardCache();
    // Notify the extension content script running on this page
    window.postMessage({ type: "CARTCONTROL_WEB_LOGOUT" }, window.location.origin);
  };

  // Listen for logout triggered by the Chrome extension
  useEffect(() => {
    const handler = (e) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      if (e.data?.type === "CARTCONTROL_EXT_LOGOUT") silentLogout();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [silentLogout]);

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
